import React from 'react'

import {
  AuthProvider,
  createUserWithEmailAndPassword,
  getAuth,
  sendEmailVerification,
  signInWithPopup,
  updateProfile,
} from 'firebase/auth'
import { useForm } from 'react-hook-form'
import { Link as ReactRouterLink, useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Spinner,
  Text,
} from '@chakra-ui/react'
import { FirebaseError } from '@firebase/util'

import { GoogleIcon } from '@/assets/icons'
import { useAuth } from '@/context/AuthContext/AuthContext'
import { googleProvider } from '@/firebase'
import {
  useCreateUser,
  useDeleteUser,
  useIsEmailTemporary,
} from '@/react-query/user'
import { useDocumentTitle } from '@/views/hooks/useDocumentTitle'

const GoogleSignUp = () => {
  const navigate = useNavigate()
  const createUser = useCreateUser()

  const signInWithProvider = (
    provider: AuthProvider,
  ): React.MouseEventHandler<HTMLButtonElement> => {
    return async (event) => {
      event.preventDefault()
      try {
        const result = await signInWithPopup(getAuth(), provider)

        if (!result.user?.displayName) {
          return navigate('/sign-up/name')
        }
        await createUser.mutateAsync()
      } catch (err) {
        console.error(err)
      }
    }
  }

  return (
    <Button
      className="mt-3 w-full"
      leftIcon={<GoogleIcon w={6} h={5} />}
      variant={'brand-solid'}
      onClick={signInWithProvider(googleProvider)}
    >
      Continue with Google
    </Button>
  )
}

interface ISignUpForm {
  name: string
  email: string
  password: string
  passwordVerify: string
}

const EmailPasswordSignUp = () => {
  const {
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
    register,
  } = useForm<ISignUpForm>()
  const isEmailTemporary = useIsEmailTemporary()
  const deleteUser = useDeleteUser()
  const createUser = useCreateUser()

  const handleEmailPasswordSignUp = async ({
    name,
    email,
    password,
    passwordVerify,
  }: ISignUpForm) => {
    try {
      if (password !== passwordVerify) {
        setError('password', {
          type: 'passwordsMismatch',
          message: 'Passwords do not match',
        })
        setError('passwordVerify', {
          type: 'passwordsMismatch',
          message: 'Passwords do not match',
        })
        return
      }

      try {
        const res = await isEmailTemporary.mutateAsync(email)
        if (res.data.isTemporary) {
          setError('email', {
            type: 'emailTemporary',
            message: 'Please use a valid email',
          })
          return
        }
      } catch (err) {
        console.error(err)
      }

      const res = await createUserWithEmailAndPassword(
        getAuth(),
        email,
        password,
      )
      await updateProfile(res.user, {
        displayName: name,
      })
      try {
        await createUser.mutateAsync()
      } catch (err) {
        setError('email', {
          type: 'badEmail',
          message: 'Please use a valid email',
        })
        await deleteUser.mutateAsync()
      }
      sendEmailVerification(res.user, {
        url: 'https://app.hirehire.ai/home',
      }).catch(console.error)
    } catch (err) {
      if (
        err instanceof FirebaseError &&
        err?.code === 'auth/email-already-in-use'
      ) {
        setError('email', {
          type: 'emailAlreadyInUse',
          message: 'Email is already in use',
        })
      }

      console.error(err)
    }
  }

  const [showPassword, setShowPassword] = React.useState(false)
  const handleShowPasswordClick = () => setShowPassword(!showPassword)

  return (
    <form onSubmit={handleSubmit(handleEmailPasswordSignUp)}>
      <FormControl mt={5} isInvalid={Boolean(errors.name)}>
        <FormLabel color={'black'} fontWeight={'bold'} htmlFor="name">
          Name
        </FormLabel>
        <Input
          id="name"
          type="name"
          autoComplete="name"
          {...register('name', {
            required: 'Name is required',
          })}
          variant={'brand-solid-white'}
        />
        <FormErrorMessage color={''}>
          {errors.name && errors.name.message}
        </FormErrorMessage>
      </FormControl>
      <FormControl mt={5} isInvalid={Boolean(errors.email)}>
        <FormLabel color={'black'} fontWeight={'bold'} htmlFor="email">
          Email
        </FormLabel>
        <Input
          id="email"
          type="email"
          autoComplete="email"
          {...register('email', {
            required: 'Email is required',
          })}
          variant={'brand-solid-white'}
        />
        <FormErrorMessage>
          {errors.email && errors.email.message}
        </FormErrorMessage>
      </FormControl>
      <FormControl mt={3} isInvalid={Boolean(errors.password)}>
        <FormLabel color={'black'} fontWeight={'bold'} htmlFor="password">
          Password
        </FormLabel>
        <InputGroup>
          <Input
            id="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            {...register('password', {
              required: 'Password is required',
              minLength: {
                value: 6,
                message: 'Password must have at least 6 characters',
              },
            })}
            variant={'brand-solid-white'}
          />
          <InputRightElement width="4.5rem">
            <Button
              h="1.75rem"
              size="sm"
              mr={2}
              variant={'brand-solid'}
              onClick={handleShowPasswordClick}
            >
              {showPassword ? 'Hide' : 'Show'}
            </Button>
          </InputRightElement>
        </InputGroup>
        <FormErrorMessage>
          {errors.password && errors.password.message}
        </FormErrorMessage>
      </FormControl>
      <FormControl mt={3} isInvalid={Boolean(errors.passwordVerify)}>
        <FormLabel color={'black'} fontWeight={'bold'} htmlFor="passwordVerify">
          Repeat password
        </FormLabel>
        <InputGroup>
          <Input
            id="passwordVerify"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            {...register('passwordVerify', {
              required: 'Password verification is required',
              minLength: {
                value: 6,
                message: 'Password must have at least 6 characters',
              },
            })}
            variant={'brand-solid-white'}
          />
          <InputRightElement width="4.5rem">
            <Button
              h="1.75rem"
              size="sm"
              variant={'brand-solid'}
              mr={2}
              onClick={handleShowPasswordClick}
            >
              {showPassword ? 'Hide' : 'Show'}
            </Button>
          </InputRightElement>
        </InputGroup>
        <FormErrorMessage>
          {errors.passwordVerify && errors.passwordVerify.message}
        </FormErrorMessage>
      </FormControl>
      <Button
        className="mt-7 w-full"
        isLoading={isSubmitting}
        type="submit"
        variant="brand-solid"
      >
        Register
      </Button>
      <Text
        fontWeight={600}
        className="text-center text-sm mt-4"
        color="gray.600"
      >
        By creating an account, you agree to the{' '}
        <Link
          href="https://hirehire.ai/privacy-policy"
          isExternal
          color="#6B5CE6"
        >
          Privacy policy
        </Link>{' '}
        and{' '}
        <Link
          href="https://hirehire.ai/terms-of-use"
          isExternal
          color="#6B5CE6"
        >
          Terms of use
        </Link>
      </Text>
    </form>
  )
}

const OrContinueWith = () => {
  return (
    <div className="mt-6 flex">
      <span className="inset-0 flex-grow flex items-center">
        <div className="w-full border-t border-[#00000033]" />
      </span>
      <div className="relative flex justify-center text-md leading-5 text-black">
        <span className="px-2">Or continue with</span>
      </div>
      <span className="inset-0 flex-grow flex items-center">
        <div className="w-full border-t border-[#00000033]" />
      </span>
    </div>
  )
}

const SignUp = () => {
  useDocumentTitle('Sign up')

  const { isLoading } = useAuth()

  return (
    <main className="w-full">
      {isLoading ? (
        <div className="flex justify-center">
          <Spinner className="w-10 h-10" />
        </div>
      ) : (
        <>
          <Heading size={'lg'} className="text-center" color={'black'}>
            Sign Up to start using hirehire
          </Heading>
          <div className="mt-2 text-center">
            <Text color={'black'}>
              Already have an account?{' '}
              <Link
                as={ReactRouterLink}
                color="#6B5CE6"
                className="font-semibold"
                to="/sign-in"
              >
                Sign in
              </Link>
            </Text>
          </div>
          <Box className="w-full flex flex-col mt-5">
            <GoogleSignUp />
            <OrContinueWith />
            <EmailPasswordSignUp />
          </Box>
        </>
      )}
    </main>
  )
}

export default SignUp
